import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine';
import {
  chartsTooltipClasses,
  ChartsTooltipContainer,
  type ChartsTooltipProps,
  useAxesTooltip,
} from '@mui/x-charts/ChartsTooltip';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { tintOnWhite } from '../../theme/colorUtils';
import { r, radii, s } from '../../theme/tokens';
import { uiTokens } from '../../theme/uiTokens';
import { chartBarSizing } from '../theme/chartBarSizing';
import { Box } from './Box';
import { formatChartDateLabel } from './chartDateLabel';
import { resolveChartPlotAreaBackgroundColor } from './chartPlotAreaBackgroundColor';
import { buildAdaptiveTickLabelInterval, buildTickLabelInterval } from './chartTickLabelInterval';
import { getChartTooltipSx } from './chartTooltipStyles';
import { darken } from './colorUtils';
import { useTheme } from './hooks';
import { Text } from './Text';
import { useBarChartPlotAreaBottomInset } from './useBarChartPlotAreaBottomInset';
import { useRecentChartItemSelection } from './useRecentChartItemSelection';
import { MuiBarChart } from './xCharts';
import type { SxProps, Theme } from '@mui/material/styles';
import type { ChartsAxisData } from '@mui/x-charts/models';
import type { MouseEvent as ReactMouseEvent } from 'react';

export type BarChartDatum = Record<string, string | number | null>;

type BarSeries = { key: string; label: string; color?: string };
type BarTooltipBreakdownRow = { label: string; value: number; color?: string };
type BarTooltipBreakdown = { total?: number; rows: BarTooltipBreakdownRow[] };
type BarTooltipBreakdownByLabel = Readonly<Record<string, BarTooltipBreakdown | undefined>>;

type BarChartProps = {
  data: BarChartDatum[];
  series: BarSeries[];
  height?: number;
  marginLeft?: number;
  marginBottom?: number;
  xAxisHeight?: number;
  yAxisWidth?: number;
  showLegend?: boolean;
  hideYAxis?: boolean;
  showPlotAreaBackground?: boolean;
  hideXAxisLine?: boolean;
  showHorizontalGrid?: boolean;
  tooltipValueSuffixByLabel?: Readonly<Record<string, string>>;
  tooltipMultiline?: boolean;
  tooltipBreakdownByLabel?: BarTooltipBreakdownByLabel;
  yAxisMin?: number;
  yAxisMax?: number;
  xTickEvery?: number;
  showAllXAxisLabels?: boolean;
  onSelectBar?: (dataIndex: number) => void;
  categoryGapRatio?: number;
  barGapRatio?: number;
  disableInteraction?: boolean;
  disableTooltip?: boolean;
  yReferenceLines?: Array<{ value: number; color: string; label?: string }>;
};

type BreakdownTooltipProps = ChartsTooltipProps & {
  breakdownByLabel: BarTooltipBreakdownByLabel;
};

type BreakdownTooltipTableProps = {
  axisFormattedValue: string;
  baseCellClassName: string;
  breakdown: BarTooltipBreakdown | undefined;
  labelCellClassName: string;
  rowClassName: string;
  seriesItems: Array<{
    color?: string;
    formattedLabel?: string | null;
    value: unknown;
  }>;
  tableClassName: string;
  valueCellClassName: string;
};

const BreakdownTooltipTable = ({
  axisFormattedValue,
  breakdown,
  labelCellClassName,
  rowClassName,
  seriesItems,
  tableClassName,
  valueCellClassName,
}: BreakdownTooltipTableProps): JSX.Element => {
  const fallbackTotal = seriesItems.reduce((sum, item) => {
    const next = typeof item.value === 'number' ? item.value : Number(item.value);
    return Number.isFinite(next) ? sum + next : sum;
  }, 0);
  const total = breakdown?.total ?? fallbackTotal;
  const rows =
    breakdown?.rows ??
    seriesItems.map((item) => ({
      label: item.formattedLabel ?? '',
      value: Number(item.value),
      color: item.color,
    }));

  return (
    <table className={tableClassName}>
      <thead>
        <tr>
          <th className={labelCellClassName}>{axisFormattedValue}</th>
          <td className={valueCellClassName}>{`Total: ${total}`}</td>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={`${axisFormattedValue}-${row.label}-${index}`} className={rowClassName}>
            <th className={labelCellClassName}>{row.label}</th>
            <td className={valueCellClassName}>{row.value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const barChartPalette = [
  uiTokens.statusColors.purple,
  uiTokens.statusColors.green,
  uiTokens.statusColors.orange,
  uiTokens.statusColors.blue,
  uiTokens.statusColors.red,
] as const;

const BreakdownTooltip = ({
  breakdownByLabel,
  classes,
  sx,
  ...props
}: BreakdownTooltipProps): JSX.Element | null => {
  const tooltipData = useAxesTooltip();
  if (!tooltipData) return null;
  const paperClassName = `${classes?.paper ?? ''} flowqa-bar-tooltip-paper`.trim();
  const tableClassName =
    `${chartsTooltipClasses.table} ${classes?.table ?? ''} flowqa-bar-tooltip-table`.trim();
  const rowClassName = chartsTooltipClasses.row;
  const cellClassName =
    `${chartsTooltipClasses.cell} ${classes?.cell ?? ''} flowqa-bar-tooltip-cell`.trim();
  const labelCellClassName = `${cellClassName} ${chartsTooltipClasses.labelCell}`.trim();
  const valueCellClassName = `${cellClassName} ${chartsTooltipClasses.valueCell}`.trim();

  return (
    <ChartsTooltipContainer classes={classes} sx={sx} {...props}>
      <div className={paperClassName}>
        {tooltipData.map(({ axisId, axisFormattedValue, seriesItems }) => {
          const breakdown = breakdownByLabel[axisFormattedValue];
          return (
            <BreakdownTooltipTable
              key={String(axisId)}
              axisFormattedValue={axisFormattedValue}
              baseCellClassName={cellClassName}
              breakdown={breakdown}
              labelCellClassName={labelCellClassName}
              rowClassName={rowClassName}
              seriesItems={seriesItems}
              tableClassName={tableClassName}
              valueCellClassName={valueCellClassName}
            />
          );
        })}
      </div>
    </ChartsTooltipContainer>
  );
};

export const BarChart = ({
  data,
  series,
  height = 260,
  marginLeft = -8,
  marginBottom,
  xAxisHeight: xAxisHeightOverride,
  yAxisWidth = 72,
  showLegend = true,
  hideYAxis = false,
  showPlotAreaBackground = true,
  hideXAxisLine = true,
  showHorizontalGrid = true,
  tooltipValueSuffixByLabel,
  tooltipMultiline = false,
  tooltipBreakdownByLabel,
  yAxisMin,
  yAxisMax,
  xTickEvery,
  showAllXAxisLabels = false,
  onSelectBar,
  categoryGapRatio,
  barGapRatio,
  disableInteraction = false,
  disableTooltip = false,
  yReferenceLines = [],
}: BarChartProps): JSX.Element => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const formatWholeNumber = useCallback((value: number | null): string => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return '';
    return Number.isInteger(value) ? String(value) : '';
  }, []);
  const formatTooltipValue = useCallback(
    (seriesKey: string, value: number | null): string => {
      if (typeof value !== 'number' || !Number.isFinite(value)) return '';
      if (seriesKey.toLowerCase() === 'passrate') {
        const rounded = value.toFixed(2).replace(/\.?0+$/, '');
        return `${rounded}%`;
      }
      return formatWholeNumber(value);
    },
    [formatWholeNumber],
  );
  const formatTooltipWithBreakdown = useCallback(
    (seriesKey: string, value: number | null, dataIndex?: number): string => {
      const base = formatTooltipValue(seriesKey, value);
      if (!tooltipValueSuffixByLabel || typeof dataIndex !== 'number' || dataIndex < 0) {
        return base;
      }
      const datum = data[dataIndex];
      const labelValue = datum.label;
      const labelKey = typeof labelValue === 'string' ? labelValue : String(labelValue);
      const suffix = tooltipValueSuffixByLabel[labelKey];
      if (!suffix) return base;
      return `${base} (${suffix})`;
    },
    [data, formatTooltipValue, tooltipValueSuffixByLabel],
  );
  const xLabels = useMemo(
    () => data.map((item) => formatChartDateLabel(String(item.label))),
    [data],
  );
  const plotAreaBackgroundColor = resolveChartPlotAreaBackgroundColor(theme);
  const xTickStep =
    typeof xTickEvery === 'number' && Number.isFinite(xTickEvery) && xTickEvery > 1
      ? Math.floor(xTickEvery)
      : null;
  const estimatedXAxisWidth =
    typeof containerWidth === 'number'
      ? Math.max(0, containerWidth - yAxisWidth - 16 - Math.abs(marginLeft))
      : null;
  const xTickLabelInterval = showAllXAxisLabels
    ? buildAdaptiveTickLabelInterval(xLabels.length, true, estimatedXAxisWidth)
    : xTickStep === null
      ? buildTickLabelInterval(xLabels.length, estimatedXAxisWidth)
      : (() => {
          const visibleLabels = new Set(
            xLabels.filter((_, index) => {
              const isStepTick = index % xTickStep === 0;
              const isLast = index === xLabels.length - 1;
              return isStepTick || isLast;
            }),
          );
          return (value: string) => visibleLabels.has(value);
        })();
  const resolvedCategoryGapRatio = categoryGapRatio ?? chartBarSizing.default.categoryGapRatio;
  const resolvedBarGapRatio = barGapRatio ?? chartBarSizing.default.barGapRatio;
  const resolvedXAxisHeight = xAxisHeightOverride ?? 32;
  const resolvedMarginBottom =
    marginBottom ?? Math.max(24, resolvedXAxisHeight + (hideXAxisLine ? 8 : 10));
  const resolveBarColor = useCallback(
    (base: string): string =>
      theme.palette.mode === 'dark' ? darken(base, 0.26) : tintOnWhite(base, 0.7),
    [theme.palette.mode],
  );
  const resolveSeriesColor = useCallback(
    (item: BarSeries, index: number): string =>
      resolveBarColor(item.color ?? barChartPalette[index % barChartPalette.length]),
    [resolveBarColor],
  );

  const barRadius = radii.lg;
  const hasLegendLabels = series.some((item) => item.label.trim().length > 0);
  const hideLegend = !showLegend || !hasLegendLabels;
  const isPassRateChart = series.some((item) => item.key.toLowerCase() === 'passrate');
  const dataMax = series.reduce((maxValue, item) => {
    const seriesMax = data.reduce((rowMax, row) => {
      const rawValue = row[item.key];
      const value = typeof rawValue === 'number' ? rawValue : null;
      return value !== null && Number.isFinite(value) && value > rowMax ? value : rowMax;
    }, 0);
    return seriesMax > maxValue ? seriesMax : maxValue;
  }, 0);
  const referenceMax = yReferenceLines.reduce((maxValue, line) => {
    if (!Number.isFinite(line.value)) return maxValue;
    return line.value > maxValue ? line.value : maxValue;
  }, 0);
  const computedYAxisMax = Math.max(dataMax, referenceMax);
  const boundedYAxisMax = isPassRateChart ? 100 : Math.max(yAxisMax ?? 0, computedYAxisMax);
  const { markRecentItemSelection, shouldSuppressAxisSelection } = useRecentChartItemSelection();
  const finiteReferenceLines = yReferenceLines.filter((line) => Number.isFinite(line.value));
  const tooltipTrigger = disableTooltip ? 'none' : undefined;
  const tooltipSxParts: Array<
    Record<string, unknown> | ((theme: Theme) => Record<string, unknown>)
  > = [getChartTooltipSx(theme) as Record<string, unknown>];
  if (tooltipMultiline) {
    tooltipSxParts.push({
      '& .MuiChartsTooltip-cell': {
        whiteSpace: 'pre-line',
        verticalAlign: 'top',
      },
      '& .MuiChartsTooltip-valueCell': {
        whiteSpace: 'pre-line',
      },
    });
  }
  if (tooltipBreakdownByLabel) {
    tooltipSxParts.push({
      '& .flowqa-bar-tooltip-paper': {
        backgroundColor: theme.app.colors.surface,
        border: `1px solid ${theme.app.colors.border}`,
        borderRadius: r('md'),
        boxShadow: 'none',
        paddingTop: '6px !important',
        paddingBottom: '2px !important',
        paddingLeft: '6px !important',
        paddingRight: '6px !important',
      },
      '& .flowqa-bar-tooltip-table': {
        borderCollapse: 'separate',
        borderSpacing: 0,
        width: 'auto !important',
        tableLayout: 'auto',
      },
      '& .flowqa-bar-tooltip-table > thead > tr > th, & .flowqa-bar-tooltip-table > thead > tr > td':
        {
          borderBottom: `1px solid ${theme.app.colors.border} !important`,
          boxShadow: 'none !important',
          paddingLeft: '4px !important',
          paddingRight: '4px !important',
          paddingTop: '0 !important',
          paddingBottom: '4px !important',
          verticalAlign: 'middle !important',
          textAlign: 'left !important',
        },
      '& .flowqa-bar-tooltip-table > thead > tr > th': {
        textAlign: 'left !important',
      },
      '& .flowqa-bar-tooltip-table > thead > tr > td': {
        textAlign: 'right !important',
        whiteSpace: 'nowrap',
      },
      '& .flowqa-bar-tooltip-table > tbody > tr > th, & .flowqa-bar-tooltip-table > tbody > tr > td':
        {
          paddingTop: '2px !important',
          paddingBottom: '2px !important',
          verticalAlign: 'middle !important',
          lineHeight: '1.1 !important',
        },
      '& .flowqa-bar-tooltip-table > tbody > tr > th': {
        textAlign: 'left !important',
        whiteSpace: 'nowrap',
      },
      '& .flowqa-bar-tooltip-table > tbody > tr > td': {
        textAlign: 'right !important',
        whiteSpace: 'nowrap',
        fontVariantNumeric: 'tabular-nums',
      },
      '& .flowqa-bar-tooltip-table > tbody > tr:first-of-type > th, & .flowqa-bar-tooltip-table > tbody > tr:first-of-type > td':
        {
          paddingTop: '8px !important',
        },
      '& .flowqa-bar-tooltip-table > tbody > tr:last-of-type > th, & .flowqa-bar-tooltip-table > tbody > tr:last-of-type > td':
        {
          paddingBottom: '4px !important',
        },
      '& .flowqa-bar-tooltip-cell, & .flowqa-bar-tooltip-table th, & .flowqa-bar-tooltip-table td':
        {
          color: theme.app.colors.text,
          fontSize: '15px',
          lineHeight: theme.typography.body2.lineHeight,
          fontWeight: theme.typography.fontWeightRegular,
        },
      '& .flowqa-bar-tooltip-table tr': {
        lineHeight: '1.1 !important',
      },
    });
  }
  const tooltipSx = tooltipSxParts as SxProps<Theme>;
  const chartMargin = hideYAxis
    ? { left: 16, right: 16, top: 16, bottom: resolvedMarginBottom }
    : { left: marginLeft, right: 16, top: 16, bottom: resolvedMarginBottom };
  const plotAreaBottomInset = useBarChartPlotAreaBottomInset({
    chartMarginBottom: chartMargin.bottom,
    containerRef,
    data,
    height,
    series,
  });
  const plotAreaLeft = hideYAxis ? chartMargin.left : chartMargin.left + yAxisWidth;
  const hiddenYAxisConfig = hideYAxis
    ? {
        position: 'none' as const,
        width: 0,
        disableLine: true,
        disableTicks: true,
        tickLabelInterval: () => false,
        tickSize: 0,
        tickNumber: 0,
        tickSpacing: 9999,
        tickLabelStyle: { display: 'none', fontSize: 0 },
        labelStyle: { display: 'none', fontSize: 0 },
      }
    : null;

  const handleItemClick = useCallback(
    (_event: ReactMouseEvent<SVGElement>, item: { dataIndex?: number }) => {
      if (!onSelectBar) return;
      const dataIndex = item.dataIndex;
      if (typeof dataIndex !== 'number' || !Number.isFinite(dataIndex)) return;
      markRecentItemSelection(dataIndex);
      onSelectBar(dataIndex);
    },
    [markRecentItemSelection, onSelectBar],
  );
  const handleAxisClick = useCallback(
    (_event: MouseEvent, axisData: ChartsAxisData | null) => {
      if (!onSelectBar) return;
      const target = _event.target;
      if (target && target instanceof Element && target.closest('.MuiBarElement-root')) {
        return;
      }
      const dataIndex = axisData?.dataIndex;
      if (typeof dataIndex !== 'number' || !Number.isFinite(dataIndex)) return;
      if (shouldSuppressAxisSelection(dataIndex)) {
        return;
      }
      onSelectBar(dataIndex);
    },
    [onSelectBar, shouldSuppressAxisSelection],
  );
  const chartSeries = useMemo(
    () =>
      series.map((item, index) => ({
        data: data.map((row) => {
          const value = row[item.key];
          return typeof value === 'number' && Number.isFinite(value) ? value : null;
        }),
        label: item.label,
        color: resolveSeriesColor(item, index),
        valueFormatter: (value: number | null, context: { dataIndex?: number }) => {
          const maybeDataIndex =
            typeof context.dataIndex === 'number' ? context.dataIndex : undefined;
          return formatTooltipWithBreakdown(item.key, value, maybeDataIndex);
        },
      })),
    [data, formatTooltipWithBreakdown, resolveSeriesColor, series],
  );
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    const updateWidth = (): void => {
      const nextWidth = container.clientWidth;
      setContainerWidth((currentWidth) => (currentWidth === nextWidth ? currentWidth : nextWidth));
    };
    updateWidth();
    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
    });
    resizeObserver.observe(container);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);
  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height,
        position: 'relative',
        overflow: 'visible',
        backgroundColor: theme.app.colors.surface,
        color: theme.app.colors.divider,
        '& .MuiChartsSurface-root': { color: theme.app.colors.divider, overflow: 'visible' },
        '& .MuiChartsSurface-root svg': { overflow: 'visible' },
        '& .MuiChartsLegend-root': { display: 'none' },
      }}
    >
      {showPlotAreaBackground ? (
        <Box
          sx={{
            position: 'absolute',
            left: Math.max(0, plotAreaLeft),
            right: chartMargin.right,
            top: chartMargin.top,
            bottom: plotAreaBottomInset ?? chartMargin.bottom,
            backgroundColor: plotAreaBackgroundColor,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
      ) : null}
      <MuiBarChart
        height={height}
        slots={
          tooltipBreakdownByLabel
            ? {
                tooltip: (tooltipProps) => {
                  const entries = Object.entries(tooltipBreakdownByLabel);
                  const breakdownByFormattedLabel = Object.fromEntries(
                    entries.map(([label, breakdown]) => [formatChartDateLabel(label), breakdown]),
                  );
                  return (
                    <BreakdownTooltip
                      {...tooltipProps}
                      breakdownByLabel={breakdownByFormattedLabel}
                    />
                  );
                },
              }
            : undefined
        }
        borderRadius={barRadius}
        onItemClick={onSelectBar && !disableInteraction ? handleItemClick : undefined}
        onAxisClick={onSelectBar && !disableInteraction ? handleAxisClick : undefined}
        series={chartSeries}
        xAxis={[
          {
            data: xLabels,
            scaleType: 'band',
            height: resolvedXAxisHeight,
            categoryGapRatio: resolvedCategoryGapRatio,
            barGapRatio: resolvedBarGapRatio,
            valueFormatter: (value) => formatChartDateLabel(String(value)),
            tickLabelInterval: xTickLabelInterval,
            tickInterval: xLabels,
            tickLabelStyle: {
              fill: theme.app.colors.textMuted,
              fontSize: '13px',
              transform: 'translateY(6px)',
            },
            tickSize: 6,
          },
        ]}
        yAxis={[
          {
            min: yAxisMin ?? 0,
            max: boundedYAxisMax > 0 ? boundedYAxisMax : undefined,
            width: hideYAxis ? 0 : yAxisWidth,
            tickLabelStyle: hideYAxis
              ? { display: 'none', fontSize: 0 }
              : { fill: theme.app.colors.textMuted, fontSize: '13px' },
            tickSize: 0,
            valueFormatter: hideYAxis ? () => '' : formatWholeNumber,
            ...(hiddenYAxisConfig ?? {}),
          },
        ]}
        grid={{ horizontal: showHorizontalGrid, vertical: false }}
        margin={chartMargin}
        slotProps={{ tooltip: { sx: tooltipSx, trigger: tooltipTrigger } }}
        sx={{
          position: 'relative',
          zIndex: 1,
          '& .MuiChartsAxis-tickLabel': { fill: theme.app.colors.textMuted },
          '& .MuiChartsAxis-directionY .MuiChartsAxis-tickLabel': {
            transform: 'translateX(-6px)',
          },
          '& .MuiChartsAxis-directionX .MuiChartsAxis-line, & .MuiChartsAxis-directionX .MuiChartsAxis-line[stroke]':
            {
              stroke: `${theme.app.colors.divider} !important`,
              strokeOpacity: 1,
            },
          '& .MuiChartsAxis-directionX .MuiChartsAxis-tick, & .MuiChartsAxis-directionX .MuiChartsAxis-tick[stroke]':
            {
              stroke: `${theme.app.colors.divider} !important`,
              strokeOpacity: 1,
            },
          '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': {
            stroke: theme.app.colors.divider,
            strokeOpacity: 1,
          },
          '& .MuiChartsGrid-horizontalLine': { stroke: theme.app.colors.divider },
          '& .MuiChartsAxis-line[stroke]': {
            stroke: `${theme.app.colors.divider} !important`,
          },
          '& .MuiChartsAxis-tick[stroke]': {
            stroke: `${theme.app.colors.divider} !important`,
          },
          '& .MuiChartsAxisHighlight-root, & .MuiChartsAxisHighlight': {
            pointerEvents: 'none',
          },
          '& .MuiChartsAxisHighlight-root *': {
            pointerEvents: 'none',
          },
          '& .MuiChartsAxisHighlight-line, & .MuiChartsAxisHighlight-rect': {
            stroke: 'transparent !important',
            fill: 'transparent !important',
            opacity: 0,
          },
          '& .MuiChartsAxisHighlightLine, & .MuiChartsAxisHighlightRect': {
            stroke: 'transparent !important',
            fill: 'transparent !important',
            opacity: 0,
          },
          ...(hideXAxisLine
            ? {
                '& .MuiChartsAxis-directionX .MuiChartsAxis-line, & .MuiChartsAxis-directionX .MuiChartsAxis-line[stroke]':
                  {
                    stroke: 'transparent !important',
                  },
              }
            : null),
          ...(hideYAxis
            ? {
                '& .MuiChartsAxis-directionY .MuiChartsAxis-line, & .MuiChartsAxis-directionY .MuiChartsAxis-line[stroke]':
                  {
                    stroke: 'transparent !important',
                  },
                '& .MuiChartsAxis-directionY .MuiChartsAxis-tick, & .MuiChartsAxis-directionY .MuiChartsAxis-tick[stroke]':
                  {
                    stroke: 'transparent !important',
                  },
                '& .MuiChartsAxis-directionY .MuiChartsAxis-tickLabel': {
                  fill: 'transparent !important',
                },
              }
            : null),
          ...(onSelectBar && !disableInteraction
            ? {
                '& .MuiChartsAxis-directionX': { cursor: 'pointer' },
                '& .MuiChartsAxis-directionX .MuiChartsAxis-tickLabel': { cursor: 'pointer' },
                '& .MuiChartsAxis-directionX .MuiChartsAxis-tick': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight-root': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight-root *': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight *': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight-line, & .MuiChartsAxisHighlight-rect': {
                  cursor: 'pointer',
                },
                '& .MuiChartsAxisHighlightLine, & .MuiChartsAxisHighlightRect': {
                  cursor: 'pointer',
                },
                '& .MuiChartsOverlay-root': { cursor: 'pointer' },
                '& svg': { cursor: 'pointer' },
              }
            : null),
          ...(disableInteraction
            ? {
                '& .MuiChartsOverlay-root': { pointerEvents: 'none' },
                '& .MuiChartsAxisHighlight-root': { pointerEvents: 'none' },
                '& .MuiChartsAxisHighlight': { pointerEvents: 'none' },
                '& .MuiBarElement-root': { pointerEvents: 'none' },
              }
            : null),
        }}
      >
        {finiteReferenceLines.map((line, index) => (
          <ChartsReferenceLine
            key={`bar-guide-${line.value}-${line.color}-${index}`}
            y={line.value}
            lineStyle={{ stroke: line.color, strokeDasharray: '8 6', strokeWidth: 0.5 }}
          />
        ))}
      </MuiBarChart>
      {!hideLegend ? (
        <Box sx={{ display: 'grid', gap: s('xs'), marginTop: s('sm') }}>
          {series.map((item, index) => (
            <Box key={item.key} sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: s('xs') }}>
                <Box
                  sx={{
                    width: s('sm'),
                    height: s('sm'),
                    backgroundColor: resolveSeriesColor(item, index),
                  }}
                />
                <Text role="body">{item.label}</Text>
              </Box>
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  );
};
